/**
 * Shared template thumbnail capture.
 *
 * Renders a template_json off-screen via PageThumbnailPreview — the same
 * component used by the editor, public template cards, and admin preview
 * fallbacks — then rasterises it with html2canvas using the export pipeline's
 * clip-path bridge so masked images come through correctly.
 *
 * Using this everywhere a thumbnail is captured guarantees the stored raster
 * matches what every other code path displays live.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import PageThumbnailPreview from '../components/Editor/PageThumbnailPreview';
import { inlineClipPathsForExport } from './exportClipPath';
import { collectFontSpecs } from '../lib/collectFonts';
import { waitForFontSpecs } from '../lib/fonts';

const DEFAULT_MAX_DIMENSION = 1080;

const waitForImages = (root) => {
  if (!root) return Promise.resolve();
  const imgs = Array.from(root.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    })
  );
};

// Wait for THIS page's specific font FACES to actually load before the raster
// is taken. `document.fonts.ready` alone is insufficient: it resolves against
// whatever was loading at call time, and the faces this page needs were only
// just requested, so they may not be registered yet. Capturing then freezes
// the browser fallback into the bitmap permanently — wider metrics that
// overflow + clip text and shift every block. waitForFontSpecs loads the CSS,
// explicitly loads each exact (family, weight, style) face the page uses via
// document.fonts.load (which resolves only once the binary is in), THEN
// awaits document.fonts.ready. Exact weights matter: loading only 400/700
// leaves e.g. a 600-weight element laying out against fallback metrics.
const waitForFonts = async (page) => {
  try {
    await waitForFontSpecs(collectFontSpecs([page]) || []);
  } catch { /* best effort — capture anyway */ }
};

/**
 * Capture a template's first page as a JPEG data URL.
 *
 * @param {object} templateJson      `{ canvas_width, canvas_height, pages: [...] }`
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=1080]  Cap on the longest side, keeps cost bounded.
 * @param {number} [opts.quality=0.85]       JPEG quality (0–1).
 * @param {number} [opts.pageIndex=0]        Which page to render.
 * @returns {Promise<string|null>} dataURL, or null on failure.
 */
export async function captureTemplateThumbnail(templateJson, opts = {}) {
  if (!templateJson?.pages?.length) return null;

  const { maxDimension = DEFAULT_MAX_DIMENSION, quality = 0.85, pageIndex = 0 } = opts;

  const page = templateJson.pages[pageIndex];
  if (!page) return null;

  const cw = templateJson.canvas_width  || templateJson.width  || 1080;
  const ch = templateJson.canvas_height || templateJson.height || 1080;

  // Cap longest side at maxDimension to keep capture cost bounded.
  const scale  = Math.min(1, maxDimension / Math.max(cw, ch));
  const targetW = Math.max(1, Math.round(cw * scale));
  const targetH = Math.max(1, Math.round(ch * scale));

  // Off-screen host. We use position:fixed + visibility:hidden so it occupies
  // no layout space but still has real width/height so PageThumbnailPreview's
  // ResizeObserver path resolves identically to the on-screen case.
  const host = document.createElement('div');
  host.setAttribute('data-thumbnail-capture', 'true');
  host.style.cssText = [
    'position: fixed',
    'left: -10000px',
    'top: 0',
    `width: ${targetW}px`,
    `height: ${targetH}px`,
    'pointer-events: none',
    'opacity: 0',
    'overflow: hidden',
    'background: transparent',
  ].join(';');
  document.body.appendChild(host);

  let root;
  try {
    root = createRoot(host);
    await new Promise((resolve) => {
      root.render(
        React.createElement(PageThumbnailPreview, {
          page,
          canvasSize: { width: cw, height: ch },
          thumbWidth: targetW,
        })
      );
      // Defer one frame so React commits before we measure.
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    await Promise.all([waitForImages(host), waitForFonts(page)]);

    // PageThumbnailPreview remounts its scaled subtree when its own fontsReady
    // flips (fresh layout with the loaded faces). That flip resolves at the
    // same moment as waitForFonts above, so give React two frames to commit
    // the remounted DOM before rasterizing.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const canvas = await html2canvas(host, {
      width: targetW,
      height: targetH,
      scale: 1,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      // Same clip-path bridge used by the editor download. PageThumbnailPreview
      // emits `clip-path: url(#…)` for masked images — without this bridge
      // html2canvas silently drops the clipping.
      onclone: (clonedDoc, clonedRoot) => {
        const cloneRoot = clonedRoot || clonedDoc;
        try { inlineClipPathsForExport(cloneRoot, host); }
        catch (e) { console.warn('[captureTemplateThumbnail] clip-path inline failed:', e); }
      },
    });

    return canvas.toDataURL('image/jpeg', quality);
  } catch (e) {
    console.warn('[captureTemplateThumbnail] capture failed:', e?.message || e);
    return null;
  } finally {
    if (root) {
      try { root.unmount(); } catch { /* ignore */ }
    }
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}

export default captureTemplateThumbnail;
